import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fetchF1DriverBenchmarks,
  fetchF1Events,
  fetchF1RaceComparison,
  fetchF1RaceHistory,
  fetchF1Standings,
} from '@/lib/api';
import {
  AA_IMPACT_LOGO_URL,
  F1_LOGO_URL,
  formatCoverageLabel,
  formatSessionLabel,
  getConstructorLogoUrl,
  getCoverageColor,
  getGradeColor,
  getRegionColor,
  getSessionLayerColor,
  getValidationColor,
} from '@/lib/constants';
import {
  Cell,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
} from 'recharts';
import {
  CalendarRange,
  ChevronRight,
  Circle,
  Flag,
  Globe2,
  Leaf,
  MapPinned,
  ShieldCheck,
  Timer,
  Trophy,
} from 'lucide-react';
import ShareScorecard from '@/components/ShareScorecard';

const SESSION_ORDER = ['sprint_qualifying', 'sprint', 'qualifying', 'race'];

export default function CompletedRaceDashboard({ isEmbed, onSelectCountry, onCompanyClick, compareIds, onCompareToggle, refreshTick }) {
  const [eventsPayload, setEventsPayload] = useState(null);
  const [selectedRaceId, setSelectedRaceId] = useState('');
  const [comparisonMap, setComparisonMap] = useState({});
  const [standings, setStandings] = useState(null);
  const [driverBenchmarks, setDriverBenchmarks] = useState(null);
  const [raceHistory, setRaceHistory] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingComparison, setLoadingComparison] = useState(false);

  useEffect(() => {
    setLoadingEvents(true);
    Promise.all([
      fetchF1Events(),
      fetchF1Standings(),
      fetchF1DriverBenchmarks({ limit: 6 }),
    ])
      .then(([eventsData, standingsData, driverBenchmarkData]) => {
        setEventsPayload(eventsData);
        setStandings(standingsData);
        setDriverBenchmarks(driverBenchmarkData);
        const latestRace = eventsData?.active?.[0]?.race_id || eventsData?.events?.[0]?.race_id || '';
        setSelectedRaceId((previous) => previous || latestRace);
      })
      .catch(console.error)
      .finally(() => setLoadingEvents(false));
  }, [refreshTick]);

  const completedEvents = useMemo(() => eventsPayload?.events || [], [eventsPayload]);
  const activeEvents = useMemo(() => eventsPayload?.active || [], [eventsPayload]);
  const trackedEvents = useMemo(() => [...activeEvents, ...completedEvents], [activeEvents, completedEvents]);
  const selectedEvent = trackedEvents.find((event) => event.race_id === selectedRaceId) || trackedEvents[0];
  const sessionSequence = useMemo(
    () => SESSION_ORDER.filter((session) => selectedEvent?.available_sessions?.includes(session)),
    [selectedEvent]
  );

  useEffect(() => {
    if (!selectedRaceId || !sessionSequence.length) return;
    setLoadingComparison(true);
    Promise.all(sessionSequence.map((session) => fetchF1RaceComparison(selectedRaceId, { session })))
      .then((responses) => {
        const mapped = {};
        responses.forEach((response) => {
          mapped[response.f1_results.session] = response;
        });
        setComparisonMap(mapped);
      })
      .catch(console.error)
      .finally(() => setLoadingComparison(false));
  }, [selectedRaceId, sessionSequence, refreshTick]);

  const latestCompletedSession = sessionSequence[sessionSequence.length - 1] || 'race';
  const latestComparison = comparisonMap[latestCompletedSession];
  const focusedCountry = latestComparison?.event?.country || selectedEvent?.country;

  useEffect(() => {
    if (!focusedCountry) return;
    fetchF1RaceHistory({ country: focusedCountry })
      .then(setRaceHistory)
      .catch(console.error);
  }, [focusedCountry, refreshTick]);

  const regionDonutData = useMemo(() => {
    const counts = completedEvents.reduce((accumulator, event) => {
      accumulator[event.region] = (accumulator[event.region] || 0) + 1;
      return accumulator;
    }, {});
    return Object.entries(counts).map(([region, count]) => ({ region, count }));
  }, [completedEvents]);

  const sessionStackData = useMemo(
    () => SESSION_ORDER.map((session) => ({ session, completed: sessionSequence.includes(session) })),
    [sessionSequence]
  );

  const topDriver = standings?.drivers?.[0];

  const buildDeltaData = (comparison) => {
    if (!comparison) return [];
    const topThreeF1Average = (comparison.f1_results.results || []).slice(0, 3).reduce((sum, entry) => sum + (entry.points || 0), 0) / 3;
    const topThreeEsgAverage = (comparison.sustainability_leaders || []).slice(0, 3).reduce((sum, entry) => sum + (entry.overall_score || 0), 0) / 3;
    return [
      { label: 'F1 Top 3 Avg Pts', value: Number.isFinite(topThreeF1Average) ? Number(topThreeF1Average.toFixed(1)) : 0, fill: '#4ECDC4' },
      { label: 'ESG Top 3 Avg Score', value: Number.isFinite(topThreeEsgAverage) ? Number(topThreeEsgAverage.toFixed(1)) : 0, fill: '#2A9D8F' },
      { label: 'Validated Companies', value: comparison.country_context?.validated_count || 0, fill: '#F1C40F' },
    ];
  };

  const getSessionHeadlineMetric = (comparison, session) => {
    const results = comparison?.f1_results?.results || [];
    if (session === 'qualifying') {
      const leader = results[0];
      return {
        label: 'Pole Position Lap',
        driver: leader?.driver_name || 'No data yet',
        value: leader?.q3 || leader?.q2 || leader?.q1 || leader?.time_or_gap || '—',
      };
    }
    if (session === 'sprint_qualifying') {
      const leader = results[0];
      return {
        label: 'Sprint Pole Lap',
        driver: leader?.driver_name || 'No data yet',
        value: leader?.q3 || leader?.q2 || leader?.q1 || leader?.time_or_gap || '—',
      };
    }
    if (session === 'sprint') {
      return {
        label: 'Sprint Winner',
        driver: comparison?.f1_results?.fastest_lap?.driver_name || results[0]?.driver_name || 'No data yet',
        value: comparison?.f1_results?.fastest_lap?.lap_time || results[0]?.time_or_gap || '—',
      };
    }
    return {
      label: 'Race Fastest Lap',
      driver: comparison?.f1_results?.fastest_lap?.driver_name || 'No data yet',
      value: comparison?.f1_results?.fastest_lap?.lap_time || '—',
    };
  };


  const scrollToSessionPanel = (session) => {
    if (typeof window === 'undefined') return;
    document.getElementById(`session-panel-${session}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loadingEvents) {
    return <div className="h-96 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />;
  }

  if (!trackedEvents.length) {
    return (
      <div className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.88)] p-8" data-testid="completed-races-empty-state">
        <h2 className="text-2xl font-bold text-white">No fully completed Grand Prix weekends available yet.</h2>
        <p className="mt-2 text-sm text-white/45">Completed mode appears only when a GP has the required finished session stack and corresponding ESG context.</p>
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="race-by-race-panel">
      <div className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.9)] p-5 shadow-[0_14px_40px_rgba(0,0,0,0.34)]">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_0.9fr] xl:items-end">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs uppercase tracking-[0.18em] text-white/35">Completed GP only mode</p>
              <Badge className="border border-white/10 bg-[#2A9D8F]/15 text-[#D8FBF7]" data-testid="completed-races-count-badge">
                {completedEvents.length} completed
              </Badge>
              {activeEvents.length ? (
                <Badge className="border border-white/10 bg-[#38bdf8]/15 text-[#D8F0FF]" data-testid="active-gps-count-badge">
                  {activeEvents.length} active
                </Badge>
              ) : null}
              <img src={F1_LOGO_URL} alt="Formula 1" className="h-4 w-auto opacity-90" />
            </div>
            <h2 className="mt-2 text-3xl font-bold text-white md:text-4xl">Actual F1 results × sustainability outcomes</h2>
            <p className="mt-2 max-w-3xl text-sm text-white/45">
              Per the completed-session logic, this canvas only displays Grand Prix weekends whose required finished sessions are available.
              ESG companies and regional context are locked to the same completed GP market. On race weekends and near-session windows, Formula 1 data refreshes every minute from the live provider layer.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { label: 'Completed regions', value: completedEvents.length, icon: CalendarRange, accent: '#3498DB' },
              { label: 'Live/active GPs', value: activeEvents.length, icon: Flag, accent: '#38bdf8' },
              { label: 'Tracked sessions', value: sessionSequence.length, icon: Timer, accent: '#FFD166' },
              { label: 'Top driver', value: topDriver?.code || '—', icon: Trophy, accent: '#D8B45A' },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-white/35">
                  <item.icon className="h-4 w-4" style={{ color: item.accent }} />
                  {item.label}
                </div>
                <p className="mt-3 font-mono text-2xl font-bold text-white tabular-nums">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <aside className="space-y-4 xl:col-span-3">
          <div className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.88)] p-4 shadow-[0_12px_32px_rgba(0,0,0,0.3)]" data-testid="completed-mode-gp-selector">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/35">
              <MapPinned className="h-4 w-4 text-[#4ECDC4]" />
              {activeEvents.length ? 'Active / recently completed GP selector' : 'Latest completed GP selector'}
            </div>
            <div className="space-y-2">
              {completedEvents.map((event) => (
                <button
                  key={event.race_id}
                  type="button"
                  onClick={() => setSelectedRaceId(event.race_id)}
                  data-testid="completed-gp-select-item"
                  className={`flex w-full items-center justify-between rounded-2xl border px-3 py-3 text-left transition-colors duration-150 ${
                    selectedRaceId === event.race_id
                      ? 'border-[#4ECDC4]/45 bg-[#4ECDC4]/12 text-white'
                      : 'border-white/10 bg-white/[0.03] text-white/60 hover:bg-white/[0.08] hover:text-white'
                  }`}
                >
                  <div>
                    <p className="font-semibold">R{event.round_number} · {event.country}</p>
                    <p className="text-xs text-white/40">{event.grand_prix}</p>
                  </div>
                  <ChevronRight className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.88)] p-4 shadow-[0_12px_32px_rgba(0,0,0,0.3)]" data-testid="session-stack">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/35">
              <Timer className="h-4 w-4 text-[#FFD166]" />
              Session stack
            </div>
            <div className="space-y-3">
              {sessionStackData.map((item) => {
                const layerColor = getSessionLayerColor(item.session);
                return (
                  <button
                    key={item.session}
                    type="button"
                    onClick={() => item.completed && scrollToSessionPanel(item.session)}
                    data-testid={`session-stack-layer-${item.session}`}
                    className="w-full rounded-2xl border border-white/10 bg-[#0B1E31] p-3 text-left"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Circle className="h-3 w-3 fill-current" style={{ color: item.completed ? layerColor : 'rgba(255,255,255,0.2)' }} />
                        <span className="text-sm font-semibold text-white">{formatSessionLabel(item.session)}</span>
                      </div>
                      <Badge className="border border-white/10 text-white" style={{ backgroundColor: item.completed ? layerColor : 'rgba(255,255,255,0.08)' }}>
                        {item.completed ? 'Complete' : 'N/A'}
                      </Badge>
                    </div>
                    <Progress
                      value={item.completed ? 100 : 0}
                      className="h-1.5 bg-white/[0.06]"
                      style={{ ['--tw-bg-opacity']: 1 }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.88)] p-4 shadow-[0_12px_32px_rgba(0,0,0,0.3)]" data-testid="region-completion-summary">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/35">
              <Globe2 className="h-4 w-4 text-[#3498DB]" />
              Region completion summary
            </div>
            <div className="grid grid-cols-[120px_1fr] gap-3">
              <div className="h-[120px] w-[120px]" data-testid="region-donut-chart">
                <PieChart width={120} height={120}>
                  <Pie data={regionDonutData} dataKey="count" nameKey="region" innerRadius={34} outerRadius={54} paddingAngle={3}>
                    {regionDonutData.map((entry) => (
                      <Cell key={entry.region} fill={getRegionColor(entry.region)} />
                    ))}
                  </Pie>
                  <RechartsTooltip contentStyle={{ backgroundColor: '#0D2238', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }} />
                </PieChart>
              </div>
              <div className="space-y-2">
                {regionDonutData.map((entry) => (
                  <div key={entry.region} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`region-legend-item-${entry.region.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <div className="flex items-center gap-2 text-sm text-white/65">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getRegionColor(entry.region) }} />
                      {entry.region}
                    </div>
                    <span className="font-mono text-sm text-white tabular-nums">{entry.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="space-y-4 xl:col-span-9">
          {sessionSequence.map((session, index) => {
            const comparison = comparisonMap[session];
            const deltaData = buildDeltaData(comparison);
            const countryContext = comparison?.country_context;
            const coverageEntries = Object.entries(countryContext?.coverage_summary || {});
            const sessionColor = getSessionLayerColor(session);
            const headlineMetric = getSessionHeadlineMetric(comparison, session);

            return (
              <motion.div
                key={session}
                id={`session-panel-${session}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04, duration: 0.22 }}
                className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.32)]"
                data-testid={`session-comparison-panel-${session}`}
              >
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="border border-white/10 text-white" style={{ backgroundColor: sessionColor }}>
                        {formatSessionLabel(session)}
                      </Badge>
                      {session === latestCompletedSession ? (
                        <Badge className="border border-white/10 bg-white/5 text-[#D8F0FF]">Latest completed session</Badge>
                      ) : null}
                      <Badge className="border border-white/10 bg-white/5 text-white/80">{selectedEvent?.country}</Badge>
                      <Badge className="border border-white/10 bg-white/5 text-white/80">{selectedEvent?.region}</Badge>
                    </div>
                    <h3 className="mt-3 text-2xl font-bold text-white">{formatSessionLabel(session)} Results vs {selectedEvent?.country} ESG Leaders</h3>
                    <p className="mt-1 text-sm text-white/45">
                      Comparing {formatSessionLabel(session).toLowerCase()} standings with top sustainability-rated companies from {selectedEvent?.country}.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShareScorecard event={selectedEvent} session={session} comparison={comparison} />
                    <Button type="button" onClick={() => onSelectCountry(focusedCountry)} className="rounded-xl bg-[#3498DB] text-white hover:bg-[#2e8cc0]" data-testid={`session-focus-market-${session}`}>
                      Focus scorecard
                    </Button>
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">{headlineMetric.label}</p>
                    <p className="mt-3 font-semibold text-white" data-testid={`session-fastest-lap-${session}`}>
                      {headlineMetric.driver}
                    </p>
                    <p className="mt-1 font-mono text-sm text-[#4ECDC4]">{headlineMetric.value}</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">ESG Market Context</p>
                    <div className="mt-3 flex items-center gap-3">
                      <img src={AA_IMPACT_LOGO_URL} alt="AA Impact Inc" className="h-8 w-8 rounded-lg object-cover" data-testid="aa-impact-esg-panel-logo" />
                      <div>
                        <p className="font-semibold text-white">{selectedEvent?.sustainability_theme || selectedEvent?.country}</p>
                        <p className="text-xs text-white/40">{selectedEvent?.target_market || `${selectedEvent?.country} market`}</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">Data Coverage</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {coverageEntries.map(([level, count]) => (
                        <Badge key={level} className="border border-white/10 text-white" style={{ backgroundColor: getCoverageColor(level) }}>
                          {formatCoverageLabel(level)} · {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">Performance Snapshot</p>
                    <div className="mt-2 space-y-2" data-testid={`session-delta-chart-${session}`}>
                      {deltaData.map((entry) => (
                        <div key={entry.label} className="flex items-center gap-2">
                          <span className="w-[90px] flex-shrink-0 text-[10px] text-white/45">{entry.label}</span>
                          <div className="flex-1 h-5 rounded bg-white/[0.05] overflow-hidden">
                            <div className="h-full rounded" style={{ width: `${Math.min(entry.value / (deltaData.reduce((m, e) => Math.max(m, e.value), 1)) * 100, 100)}%`, backgroundColor: entry.fill }} />
                          </div>
                          <span className="w-[36px] text-right font-mono text-[10px] text-white/60">{entry.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-[#0B1E31] p-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                      <img src={F1_LOGO_URL} alt="Formula 1" className="h-4 w-auto opacity-90" data-testid="f1-results-logo" />
                      {formatSessionLabel(session)} Results
                    </div>
                    <ScrollArea className="h-[360px] pr-2">
                      <Table data-testid={`session-results-table-${session}`}>
                        <TableHeader className="sticky top-0 bg-[#0B1E31]">
                          <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="text-white/45">POS</TableHead>
                            <TableHead className="text-white/45">DRIVER</TableHead>
                            <TableHead className="text-white/45">{session === 'qualifying' || session === 'sprint_qualifying' ? 'BEST LAP' : 'TIME'}</TableHead>
                            <TableHead className="text-white/45">START</TableHead>
                            <TableHead className="text-white/45">PTS</TableHead>
                            <TableHead className="text-white/45">DETAILS</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(comparison?.f1_results?.results || []).map((result) => {
                            const logoUrl = getConstructorLogoUrl(result.team_name);
                            return (
                              <TableRow key={`${session}-${result.driver_number}-${result.position || result.driver_code}`} className="border-white/10 hover:bg-white/[0.04]">
                                <TableCell className="font-mono text-white tabular-nums">{result.position || '—'}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {result.team_logo || logoUrl ? <img src={result.team_logo || logoUrl} alt={result.team_name} className="h-4 w-4 object-contain opacity-90" /> : null}
                                    {result.driver_image ? <img src={result.driver_image} alt={result.driver_name} className="h-6 w-6 rounded-full object-cover" /> : null}
                                    <div>
                                      <p className="font-semibold text-white">{result.driver_code || result.driver_name}</p>
                                      <p className="text-xs text-white/40">{result.team_name}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="font-mono text-xs text-white/70 tabular-nums">
                                    <p>{result.time_or_gap || '—'}</p>
                                    {result.fastest_lap && result.fastest_lap_time ? <p className="mt-0.5 text-[#4ECDC4]">FL {result.fastest_lap_time}</p> : null}
                                    {!result.fastest_lap && (result.q1 || result.q2 || result.q3) ? (
                                      <p className="mt-0.5 text-white/30 text-[10px]">
                                        {result.q1 ? `Q1 ${result.q1}` : ''}{result.q2 ? ` · Q2 ${result.q2}` : ''}{result.q3 ? ` · Q3 ${result.q3}` : ''}
                                      </p>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono text-white/70 tabular-nums">{result.grid_position || '—'}</TableCell>
                                <TableCell className="font-mono text-[#D8B45A] tabular-nums">{result.points}</TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1.5 text-xs">
                                    {result.fastest_lap ? <span className="rounded bg-[#4ECDC4]/15 px-1.5 py-0.5 font-semibold text-[#4ECDC4]">FL</span> : null}
                                    {result.pit_stops ? <span className="text-white/45">Pit {result.pit_stops}</span> : null}
                                    {result.position_change !== null && result.position_change !== undefined ? (
                                      <span className={`${result.position_change > 0 ? 'text-[#3EB489]' : result.position_change < 0 ? 'text-[#c0152f]' : 'text-white/45'}`}>
                                        {result.position_change > 0 ? '+' : ''}{result.position_change}
                                      </span>
                                    ) : null}
                                    {result.status && result.status !== 'Completed' ? <span className="text-[#c0152f]">{result.status}</span> : null}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-[#0B1E31] p-4">
                    <div className="mb-3 flex items-center gap-3">
                      <img src={AA_IMPACT_LOGO_URL} alt="AA Impact Inc" className="h-8 w-8 rounded-lg object-cover" />
                      <div>
                        <p className="text-sm font-semibold text-white">{selectedEvent?.country} Sustainability Leaders</p>
                        <p className="text-xs text-white/45">Top-rated ESG companies from the {selectedEvent?.country} market for this GP.</p>
                      </div>
                    </div>
                    <ScrollArea className="h-[360px] pr-2">
                      <Table data-testid={`session-esg-panel-${session}`}>
                        <TableHeader className="sticky top-0 bg-[#0B1E31]">
                          <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="text-white/45">RANK</TableHead>
                            <TableHead className="text-white/45">COMPANY</TableHead>
                            <TableHead className="text-white/45">ESG SCORE</TableHead>
                            <TableHead className="text-white/45">SOURCE</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(comparison?.sustainability_leaders || []).map((company, index) => (
                            <TableRow key={`${session}-${company.id}`} className="border-white/10 hover:bg-white/[0.04]">
                              <TableCell className="font-mono text-white tabular-nums">{index + 1}</TableCell>
                              <TableCell>
                                <button type="button" onClick={() => onCompanyClick(company)} className="text-left">
                                  <p className="font-semibold text-white">{company.name}</p>
                                  <p className="text-xs text-white/40">{company.hq_country} · {company.sector}</p>
                                </button>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <span className="font-mono text-white tabular-nums" style={{ color: getGradeColor(company.grade) }}>{company.overall_score}</span>
                                  <Badge className="w-fit border border-white/10 text-white" style={{ backgroundColor: getValidationColor(company.validation_status) }}>
                                    {company.validation_status.replace('_', ' ')}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-2 text-xs text-white/55">
                                  <p>{formatCoverageLabel(company.coverage_level)} · {company.gp_country}</p>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onCompareToggle(company.id)}
                                    className={`rounded-full border-white/10 ${compareIds.includes(company.id) ? 'bg-[#2A9D8F]/18 text-[#D8FBF7]' : 'bg-white/[0.03] text-white/70 hover:bg-white/[0.08]'}`}
                                  >
                                    {compareIds.includes(company.id) ? 'Selected' : 'Compare'}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>
                </div>
              </motion.div>
            );
          })}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.32)]" data-testid="driver-benchmark-panel">
              <div className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/35">
                <Trophy className="h-4 w-4 text-[#D8B45A]" />
                Driver performance benchmarks
              </div>
              <div className="space-y-3">
                {(driverBenchmarks?.drivers || []).slice(0, 4).map((entry) => {
                  const profile = entry.profile || {};
                  const current = entry.current || {};
                  return (
                    <div key={current.code || profile.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                      <div className="flex items-start gap-3">
                        {profile.image ? <img src={profile.image} alt={profile.name} className="h-12 w-12 rounded-xl object-cover" /> : null}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="font-semibold text-white">{profile.name || current.name}</p>
                              <p className="text-xs text-white/45">{current.team_name || profile.teams?.[0]?.team?.name || 'Driver benchmark'}</p>
                            </div>
                            <Badge className="border border-white/10 bg-white/5 text-[#D8F0FF]">P{current.position}</Badge>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-white/60">
                            <div>
                              <p className="text-white/35">Current points</p>
                              <p className="font-mono text-white tabular-nums">{current.points}</p>
                            </div>
                            <div>
                              <p className="text-white/35">Vs 2024 points</p>
                              <p className="font-mono text-white tabular-nums">{entry.benchmark_vs_last_season_points ?? '—'}</p>
                            </div>
                            <div>
                              <p className="text-white/35">World titles</p>
                              <p className="font-mono text-white tabular-nums">{profile.world_championships ?? '—'}</p>
                            </div>
                            <div>
                              <p className="text-white/35">Career points</p>
                              <p className="font-mono text-white tabular-nums">{profile.career_points ?? '—'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.32)]" data-testid="race-history-panel">
              <div className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/35">
                <Flag className="h-4 w-4 text-[#38bdf8]" />
                Published race history
              </div>
              <ScrollArea className="h-[320px] pr-2">
                <div className="space-y-3">
                  {(raceHistory?.races || []).slice(0, 6).map((race) => (
                    <div key={`${race.season}-${race.race_id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="font-semibold text-white">{race.season} · {race.competition}</p>
                          <p className="text-xs text-white/45">{race.city}, {race.country}</p>
                        </div>
                        <Badge className="border border-white/10 bg-white/5 text-[#D8F0FF]">{new Date(race.date).toLocaleDateString()}</Badge>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-white/60">
                        <div>
                          <p className="text-white/35">Winner</p>
                          <p className="font-medium text-white">{race.winner?.driver?.abbr || race.winner?.driver?.name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-white/35">Pole</p>
                          <p className="font-medium text-white">{race.pole?.driver?.abbr || race.pole?.driver?.name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-white/35">Fastest lap</p>
                          <p className="font-medium text-white">{race.fastest_lap?.driver?.abbr || race.fastest_lap?.driver?.name || '—'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
